<template>
    <div class="flex flex-col h-full w-full bg-transparent text-white text-xs overflow-hidden">
        <!-- Script tabs -->
        <div class="flex items-center gap-1 px-2 pt-2 pb-0 border-b border-white/20 bg-white/10 flex-shrink-0 flex-wrap">
            <button
                v-for="name in SYSTEM_SCRIPTS"
                :key="name"
                class="px-3 py-1.5 rounded-t text-xs transition-colors"
                :class="activeScript === name
                    ? 'bg-white/15 text-white border border-b-0 border-white/20'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'"
                @click="switchScript(name)">
                {{ name }}.cmd
            </button>
            <button
                v-for="cs in flowStore.customScripts"
                :key="cs.name"
                class="px-3 py-1.5 rounded-t text-xs transition-colors flex items-center gap-1"
                :class="activeScript === cs.name
                    ? 'bg-white/15 text-white border border-b-0 border-white/20'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'"
                @click="switchScript(cs.name)">
                {{ cs.name }}.cmd
                <span class="text-red-400 hover:text-red-300 ml-1" title="Remove script"
                    @click.stop="removeScript(cs.name)">×</span>
            </button>
            <button class="px-2 py-1 text-green-400 hover:text-green-300 text-lg leading-none" title="Add custom script"
                @click="promptNewScript">+</button>

            <!-- view toggle (right side) -->
            <div class="ml-auto flex items-center gap-0.5 pr-1">
                <button
                    class="px-2 py-1 rounded text-[10px] transition-colors"
                    :class="viewMode === 'table' ? 'bg-white/15 text-white' : 'text-gray-500 hover:text-gray-300'"
                    title="Table view"
                    @click="viewMode = 'table'">⊞ Table</button>
                <button
                    class="px-2 py-1 rounded text-[10px] transition-colors"
                    :class="viewMode === 'flow' ? 'bg-white/15 text-white' : 'text-gray-500 hover:text-gray-300'"
                    title="Flow graph view"
                    @click="viewMode = 'flow'">⌸ Flow</button>
            </div>
        </div>

        <div class="flex flex-1 overflow-hidden">
            <!-- Node palette — flow view only -->
            <div v-if="viewMode === 'flow'"
                class="w-40 flex-shrink-0 border-r border-white/20 bg-white/10 overflow-y-auto p-2 space-y-3">
                <div v-for="cat in NODE_CATEGORIES" :key="cat.label">
                    <p class="text-[10px] font-bold uppercase tracking-wider mb-1"
                        :style="{ color: cat.color }">{{ cat.label }}</p>
                    <div class="space-y-1">
                        <button
                            v-for="n in cat.nodes"
                            :key="n.paletteId ?? n.type"
                            class="w-full text-left px-2 py-1.5 rounded text-xs text-gray-200 hover:text-white transition-colors cursor-grab active:cursor-grabbing border border-transparent hover:border-white/30"
                            :style="{ background: cat.color + '22' }"
                            draggable="true"
                            @dragstart="onPaletteDragStart($event, n)">
                            <span class="mr-1">{{ n.icon }}</span>{{ n.label }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Table view — wrapper owns flex-1 (ScriptTableView has 2 roots so class-inherit fails) -->
            <div v-if="viewMode === 'table' && currentFlow" class="flex-1 min-w-0 overflow-hidden">
                <ScriptTableView :flow="currentFlow" :script-name="activeScript" class="h-full" />
            </div>

            <!-- Flow graph view -->
            <div v-if="viewMode === 'flow'" class="flex-1 relative" ref="flowWrapper" @dragover.prevent @drop="onCanvasDrop">
                <VueFlow
                    v-if="currentFlow"
                    :key="activeScript"
                    v-model:nodes="vfNodes"
                    v-model:edges="vfEdges"
                    :node-types="nodeTypes"
                    :default-viewport="{ zoom: 1 }"
                    fit-view-on-init
                    class="w-full h-full"
                    @connect="onConnect">
                    <Background :bg-color="flowBg" :pattern-color="flowGrid" :gap="20" />
                    <Controls />
                    <MiniMap node-color="#475569" :style="{ background: flowMinimap }" />
                </VueFlow>
            </div>

            <!-- Preview / actions panel (right) — wider in table mode (no palette) -->
            <div class="flex-shrink-0 border-l border-white/20 bg-white/10 flex flex-col overflow-hidden"
                :class="viewMode === 'table' ? 'w-80' : 'w-56'">
                <div class="px-3 py-2 border-b border-white/20 font-semibold text-gray-300 flex-shrink-0">
                    Preview
                </div>
                <VAceEditor
                    :value="previewCmd || '# empty script'"
                    lang="sh"
                    :theme="editorStore.theme"
                    class="flex-1 min-h-0"
                    :options="aceOptions" />
                <div class="p-2 space-y-1.5 border-t border-white/20">
                    <button v-if="settings?.tauri"
                        class="w-full py-1.5 rounded bg-ui-active hover:opacity-90 text-white text-xs transition-colors"
                        :disabled="isFetching || isUploading"
                        @click="fetchFromPlate">
                        {{ isFetching ? 'Fetching...' : `Fetch from ${settings?.plate?.name ?? 'plate'}` }}
                    </button>
                    <button v-if="settings?.tauri"
                        class="w-full py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs transition-colors"
                        :disabled="isUploading || isFetching"
                        @click="uploadCurrentScript">
                        {{ isUploading ? 'Uploading...' : `Upload to ${settings?.plate?.name ?? 'plate'}` }}
                    </button>
                    <button
                        class="w-full py-1.5 rounded bg-white/15 hover:bg-white/25 text-white text-xs transition-colors"
                        @click="downloadCurrentScript">
                        Download .cmd
                    </button>
                    <div v-if="uploadStatus" class="text-[10px]"
                        :class="uploadStatus.startsWith('Error') || uploadStatus.startsWith('Not found') ? 'text-red-400' : 'text-green-400'">
                        {{ uploadStatus }}
                    </div>
                </div>
            </div>
        </div>

        <!-- New script name dialog -->
        <Teleport to="body">
            <div v-if="showNewScriptDialog"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
                @click.self="cancelNewScript">
                <div class="bg-[#1e1b2e] border border-white/20 rounded-lg p-5 w-72 shadow-xl">
                    <h3 class="text-sm font-semibold text-white mb-3">Add custom script</h3>
                    <input
                        ref="newScriptInput"
                        v-model="newScriptName"
                        type="text"
                        placeholder="e.g. startup"
                        class="w-full px-3 py-1.5 rounded bg-white/10 border border-white/20 text-white text-xs placeholder-gray-500 focus:outline-none focus:border-white/40 mb-4"
                        @keydown.enter="confirmNewScript"
                        @keydown.esc="cancelNewScript" />
                    <div class="flex justify-end gap-2">
                        <button class="px-4 py-1.5 rounded text-xs bg-white/10 hover:bg-white/20 text-gray-300 transition-colors"
                            @click="cancelNewScript">Cancel</button>
                        <button class="px-4 py-1.5 rounded text-xs bg-blue-600 hover:bg-blue-500 text-white transition-colors"
                            @click="confirmNewScript">Add</button>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, computed, provide, watch, markRaw, toRaw, nextTick } from 'vue';
import { interpolateColor } from '../utils/color.js';
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';

import {
    bytesToBase64,
    downloadDeviceFile,
    listDeviceFiles,
    uploadDeviceFile,
} from '../services/httpTransfer';

import { flowStore, SYSTEM_SCRIPTS, setFlow, addCustomScript, removeCustomScript, getFlow } from './useFlowStore.js';
import ScriptTableView from './ScriptTableView.vue';
import { useHaspObjects } from './useHaspObjects.js';
import { HaspTemplatePage } from '../Hasp/HaspPage';
import { compileFlow } from './ScriptCompiler.js';
import { parseScript } from './FlowParser.js';
import { NODE_CATEGORIES, createNode } from './nodePalette.js';

import TriggerNode from './nodes/TriggerNode.vue';
import GotoPageNode from './nodes/GotoPageNode.vue';
import ClearPageNode from './nodes/ClearPageNode.vue';
import SetPropertyNode from './nodes/SetPropertyNode.vue';
import BacklightNode from './nodes/BacklightNode.vue';
import MoodlightNode from './nodes/MoodlightNode.vue';
import OutputNode from './nodes/OutputNode.vue';
import DelayNode from './nodes/DelayNode.vue';
import RunScriptNode from './nodes/RunScriptNode.vue';
import RebootNode from './nodes/RebootNode.vue';
import ScreenshotNode from './nodes/ScreenshotNode.vue';
import IdleNode from './nodes/IdleNode.vue';
import JsonlNode from './nodes/JsonlNode.vue';
import TemplateRefNode from './nodes/TemplateRefNode.vue';
import CommandNode from './nodes/CommandNode.vue';

import { VAceEditor } from 'vue3-ace-editor';
import { useEditorStore } from '../stores/editorStore';
import 'ace-builds/src-noconflict/mode-sh';
import '/src/ace-config';

const editorStore = useEditorStore();

const aceOptions = {
    readOnly: false,
    showPrintMargin: false,
    showGutter: false,
    highlightActiveLine: false,
    fontSize: 11,
    wrap: true,
    useWorker: false,
};

const props = defineProps({
    layer: Object,
    settings: Object,
    popupState: Boolean,
});

const flowBg      = computed(() => props.settings?.ui?.main_color ?? '#092e53');
const flowGrid    = computed(() => interpolateColor(flowBg.value, '#ffffff', 0.15));
const flowMinimap = computed(() => interpolateColor(flowBg.value, '#ffffff', 0.08));

// --- node type registry (markRaw avoids Vue proxy overhead on components)
const nodeTypes = {
    trigger: markRaw(TriggerNode),
    gotoPage: markRaw(GotoPageNode),
    clearPage: markRaw(ClearPageNode),
    setProperty: markRaw(SetPropertyNode),
    backlight: markRaw(BacklightNode),
    moodlight: markRaw(MoodlightNode),
    output: markRaw(OutputNode),
    delay: markRaw(DelayNode),
    runScript: markRaw(RunScriptNode),
    reboot: markRaw(RebootNode),
    screenshot: markRaw(ScreenshotNode),
    idle: markRaw(IdleNode),
    jsonl: markRaw(JsonlNode),
    templateRef: markRaw(TemplateRefNode),
    command: markRaw(CommandNode),
};

// --- haspObjects provided to nodes via inject
const layerRef = computed(() => props.layer);
const tick = ref(0); // increment to force Konva-backed computeds to re-run
const haspObjects = useHaspObjects(layerRef, tick);
provide('haspObjects', haspObjects);

// --- view mode: 'table' (default) or 'flow'
const viewMode = ref('table');

// --- active script tab
const activeScript = ref(SYSTEM_SCRIPTS[0]);
provide('activeScript', activeScript);

// --- new script dialog
const showNewScriptDialog = ref(false);
const newScriptName = ref('');
const newScriptInput = ref(null);

// --- createTemplatePage: creates a template page in the layer, auto-selects it in the node
function createTemplatePage(suggestedName, targetPageId = null) {
    if (!props.layer) return null;
    const page = props.layer.addPage({
        obj:        'template_page',
        comment:    suggestedName,
        targetPage: targetPageId,
    });
    tick.value++; // force templatePages computed to re-run
    if (page && targetPageId !== null) {
        props.layer.updateTemplateUnderlay();
    }
    return page?.attrs?.haspid ?? null;
}
provide('createTemplatePage', createTemplatePage);

const currentFlow = computed(() => {
    if (SYSTEM_SCRIPTS.includes(activeScript.value)) {
        return flowStore.flows[activeScript.value];
    }
    return flowStore.customScripts.find(s => s.name === activeScript.value) ?? null;
});

function switchScript(name) {
    activeScript.value = name;
}

function promptNewScript() {
    newScriptName.value = '';
    showNewScriptDialog.value = true;
    nextTick(() => newScriptInput.value?.focus());
}

function confirmNewScript() {
    const name = newScriptName.value.trim();
    if (!name) return;
    showNewScriptDialog.value = false;
    addCustomScript(name);
    activeScript.value = name.replace(/[^a-z0-9_-]/gi, '_');
}

function cancelNewScript() {
    showNewScriptDialog.value = false;
}

function removeScript(name) {
    if (!window.confirm(`Remove script "${name}.cmd"?`)) return;
    removeCustomScript(name);
    if (activeScript.value === name) activeScript.value = SYSTEM_SCRIPTS[0];
}

// v-model bindings: Vue Flow's two-way sync writes internal state (including
// updateNodeData changes from node components) back into the store, keeping
// previewCmd reactive to all node data mutations.
const vfNodes = computed({
    get: () => currentFlow.value?.nodes ?? [],
    set: (val) => { if (currentFlow.value) currentFlow.value.nodes = val; },
});
const vfEdges = computed({
    get: () => currentFlow.value?.edges ?? [],
    set: (val) => { if (currentFlow.value) currentFlow.value.edges = val; },
});

function onConnect(connection) {
    if (!currentFlow.value) return;
    const flow = currentFlow.value;
    // Enforce one outgoing edge per source handle
    const existing = flow.edges.findIndex(
        e => e.source === connection.source && e.sourceHandle === connection.sourceHandle
    );
    if (existing >= 0) flow.edges.splice(existing, 1);

    flow.edges.push({
        id: `e-${connection.source}-${connection.target}`,
        source: connection.source,
        sourceHandle: connection.sourceHandle ?? 'output',
        target: connection.target,
        targetHandle: connection.targetHandle ?? 'input',
        animated: true,
        style: { stroke: '#94a3b8', strokeWidth: 2 },
    });
}

// --- drag-and-drop from palette
const flowWrapper = ref(null);
let draggedPaletteNode = null;

function onPaletteDragStart(event, nodeDef) {
    draggedPaletteNode = nodeDef;
    event.dataTransfer.effectAllowed = 'copy';
}

function onCanvasDrop(event) {
    if (!draggedPaletteNode || !currentFlow.value || !flowWrapper.value) return;
    const rect = flowWrapper.value.getBoundingClientRect();
    // Approximate canvas position (ignores current zoom/pan — good enough for drop)
    const x = event.clientX - rect.left - 90;
    const y = event.clientY - rect.top - 30;
    const node = createNode(draggedPaletteNode.type, { ...draggedPaletteNode.defaultData }, { x, y });
    currentFlow.value.nodes.push(node);
    draggedPaletteNode = null;
}

// --- compiled preview (reads vfNodes/vfEdges — the live v-model refs — so any
// updateNodeData call from a node component triggers a recompute)
const previewCmd = computed(() => {
    if (!currentFlow.value) return '';
    const lines = compileFlow(vfNodes.value, vfEdges.value, props.layer);
    return lines.join('\n');
});

// --- hydrateJsonlNodes: after fetching a script, replace jsonl nodes that contain
// valid JSON (with an obj field) with templateRef nodes, creating the template page
// and adding the objects to it. Contiguous runs of jsonl nodes collapse into one ref.
function hydrateJsonlNodes(nodes, edges, scriptName) {
    // Parse payloads upfront; only keep nodes that are valid OpenHASP object descriptors
    const jsonlParsed = new Map(); // nodeId -> parsed JSON
    for (const n of nodes) {
        if (n.type !== 'jsonl') continue;
        try {
            const parsed = JSON.parse(n.data.payload ?? '');
            if (parsed && typeof parsed === 'object' && parsed.obj) {
                jsonlParsed.set(n.id, parsed);
            }
        } catch { /* not valid JSON — leave as jsonl node */ }
    }
    if (jsonlParsed.size === 0 || !props.layer) return { nodes, edges };

    // Build chain maps
    const nextMap = {}, prevMap = {};
    for (const e of edges) { nextMap[e.source] = e.target; prevMap[e.target] = e.source; }

    // Walk chain from trigger to get ordered IDs
    const targetIds = new Set(edges.map(e => e.target));
    const triggerNode = nodes.find(n => n.type === 'trigger' && !targetIds.has(n.id));
    const orderedIds = [];
    if (triggerNode) {
        let cur = triggerNode.id;
        const visited = new Set();
        while (cur && !visited.has(cur)) { visited.add(cur); orderedIds.push(cur); cur = nextMap[cur]; }
    }

    // Find contiguous groups of jsonl nodes in chain order
    const groups = [];
    let currentGroup = null;
    for (const id of orderedIds) {
        if (jsonlParsed.has(id)) {
            if (!currentGroup) {
                currentGroup = { startPrev: prevMap[id] ?? null, nodeIds: [id] };
            } else {
                currentGroup.nodeIds.push(id);
            }
        } else if (currentGroup) {
            const lastId = currentGroup.nodeIds[currentGroup.nodeIds.length - 1];
            groups.push({ ...currentGroup, endNext: nextMap[lastId] ?? null });
            currentGroup = null;
        }
    }
    if (currentGroup) {
        const lastId = currentGroup.nodeIds[currentGroup.nodeIds.length - 1];
        groups.push({ ...currentGroup, endNext: nextMap[lastId] ?? null });
    }
    if (groups.length === 0) return { nodes, edges };

    // Find or create one shared template page for this script
    const templateName = `Template ${scriptName}`;
    const rawLayer = toRaw(props.layer);
    let templatePageHaspid = null;

    const existingPage = rawLayer.getChildren().find(c => {
        const rc = toRaw(c);
        return rc instanceof HaspTemplatePage && rc.attrs.comment === templateName;
    });
    if (existingPage) {
        templatePageHaspid = toRaw(existingPage).attrs.haspid;
    } else {
        templatePageHaspid = createTemplatePage(templateName);
        if (templatePageHaspid === null) return { nodes, edges };
    }

    // Collect haspids already on the template page to avoid duplicates on re-fetch
    const existingHaspids = new Set();
    const rawLayer2 = toRaw(props.layer);
    const tp = rawLayer2.getChildren().find(c => {
        const rc = toRaw(c);
        return rc instanceof HaspTemplatePage && rc.attrs.haspid === templatePageHaspid;
    });
    if (tp) {
        toRaw(tp).getChildren().forEach(ch => { if (ch.haspid !== undefined) existingHaspids.add(ch.haspid); });
    }

    // Add objects from every group to the template page
    const screenW = props.settings?.plate?.width ?? 320;
    const screenH = props.settings?.plate?.height ?? 240;

    // Objects whose size/position are self-managed by their constructor when given
    // _pageWidth/_pageHeight — don't force w/h for these.
    const SELF_SIZING_OBJS = new Set(['msgbox', 'spinner', 'calendar', 'keyboard']);

    for (const group of groups) {
        for (const nodeId of group.nodeIds) {
            const jsonObj = jsonlParsed.get(nodeId);
            if (jsonObj && !existingHaspids.has(jsonObj.id)) {
                const selfSizing = SELF_SIZING_OBJS.has(jsonObj.obj);
                const importConfig = {
                    ...jsonObj,
                    page: templatePageHaspid,
                    // Provide page dims so constructors can auto-size and centre
                    _pageWidth:  screenW,
                    _pageHeight: screenH,
                };
                if (!selfSizing) {
                    // Provide fallback geometry for objects that don't self-size
                    importConfig.x = jsonObj.x ?? 0;
                    importConfig.y = jsonObj.y ?? 0;
                    importConfig.w = jsonObj.w ?? Math.round(screenW * 0.5);
                    importConfig.h = jsonObj.h ?? Math.round(screenH * 0.15);
                }
                rawLayer2.importObject(importConfig);
                existingHaspids.add(jsonObj.id);
            }
        }
    }
    tick.value++;

    // Rebuild nodes/edges: remove jsonl groups, insert templateRef per group
    const removedIds = new Set(groups.flatMap(g => g.nodeIds));
    const newNodes = nodes.filter(n => !removedIds.has(n.id));
    const newEdges = edges.filter(e => !removedIds.has(e.source) && !removedIds.has(e.target));

    const baseEdge = { animated: true, style: { stroke: '#94a3b8', strokeWidth: 2 } };
    for (const group of groups) {
        const firstNode = nodes.find(n => n.id === group.nodeIds[0]);
        const refNode = createNode('templateRef', { templatePageId: templatePageHaspid }, firstNode?.position ?? { x: 400, y: 200 });
        newNodes.push(refNode);
        if (group.startPrev) {
            newEdges.push({ ...baseEdge, id: `e-${group.startPrev}-${refNode.id}`, source: group.startPrev, sourceHandle: 'output', target: refNode.id, targetHandle: 'input' });
        }
        if (group.endNext) {
            newEdges.push({ ...baseEdge, id: `e-${refNode.id}-${group.endNext}`, source: refNode.id, sourceHandle: 'output', target: group.endNext, targetHandle: 'input' });
        }
    }

    return { nodes: newNodes, edges: newEdges };
}

// --- fetch from plate
const isFetching = ref(false);

async function fetchFromPlate() {
    if (!props.settings?.tauri || !currentFlow.value) return;
    isFetching.value = true;
    uploadStatus.value = '';
    try {
        const filename = scriptFilename(activeScript.value);

        // Check the file exists on the plate first
        const fileList = await listDeviceFiles(props.settings.plate);
        const found = fileList.find(f => f.filename === filename);
        if (!found) {
            uploadStatus.value = `Not found on plate: ${filename}`;
            return;
        }

        const fileData = await downloadDeviceFile(props.settings.plate, `L:/${filename}`);

        // fileData.data is a byte array — decode to UTF-8 text
        const text = new TextDecoder('utf-8').decode(new Uint8Array(fileData.data));

        // Preserve trigger node id so existing edges aren't orphaned
        const triggerId = currentFlow.value.nodes.find(n => n.type === 'trigger')?.id;
        const parsed = parseScript(text, activeScript.value, triggerId);
        const { nodes, edges } = hydrateJsonlNodes(parsed.nodes, parsed.edges, activeScript.value);

        currentFlow.value.nodes = nodes;
        currentFlow.value.edges = edges;
        uploadStatus.value = `Loaded ${filename} (${nodes.length - 1} command${nodes.length !== 2 ? 's' : ''})`;
    } catch (e) {
        uploadStatus.value = `Error: ${e}`;
    } finally {
        isFetching.value = false;
    }
}

// --- upload / download
const isUploading = ref(false);
const uploadStatus = ref('');

function scriptFilename(name) {
    return `${name}.cmd`;
}

function cmdContent() {
    return `# ${scriptFilename(activeScript.value)} — generated by OpenHASP Designer\n${previewCmd.value}\n`;
}

async function uploadCurrentScript() {
    if (!props.settings?.tauri) return;
    isUploading.value = true;
    uploadStatus.value = '';
    try {
        const content = cmdContent();
        const bytes = new TextEncoder().encode(content);
        await uploadDeviceFile(
            props.settings.plate,
            `L:/${scriptFilename(activeScript.value)}`,
            bytesToBase64(bytes)
        );
        uploadStatus.value = `Uploaded ${scriptFilename(activeScript.value)}`;
    } catch (e) {
        uploadStatus.value = `Error: ${e}`;
    } finally {
        isUploading.value = false;
    }
}

function downloadCurrentScript() {
    const content = cmdContent();
    const blob = new Blob([content], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = scriptFilename(activeScript.value);
    a.click();
}
</script>

<style>
/* Override Vue Flow dark theme to match app palette */
.vue-flow__background { background: #0f172a !important; }
.vue-flow__edge-path { stroke: #94a3b8; }
.vue-flow__handle {
    background: #94a3b8 !important;
    border: 2px solid #0f172a !important;
    width: 10px !important;
    height: 10px !important;
}
.vue-flow__handle:hover { background: #e2e8f0 !important; }
.vue-flow__controls button { background: #1e293b; color: #e2e8f0; border-color: #475569; }
.vue-flow__controls button:hover { background: #334155; }
.vue-flow__minimap { border: 1px solid #475569; }
</style>
